import { useState } from 'react';
import { ArrowLeft, ChevronRight, Lightbulb, Lock } from 'lucide-react';
import { Button } from '@/shared/components/shadcn/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/shadcn/select';
import { cn } from '@/shared/utils';
import { useWorkspace } from '@/shared/hooks/use-workspace';
import { beautySupplementPlanning } from '../data/beauty-supplement-planning';
import { BenchmarkSelector, type BenchmarkSelection } from '../components/benchmark-selector';
import { PlanningResults } from '../components/planning-results';
import { StepProgressLoader } from '../components/step-progress-loader';
import type { SalesPlatform } from '../data/benchmark-brands';

type PlanningStep = 'input' | 'loading-12' | 'benchmark' | 'loading-23' | 'results';

export interface FormValues {
  country: string;
  platform: string;
  brandProduct: string;
  metric: string;
  salesPlatform: SalesPlatform;
}

const COUNTRY_OPTIONS = [
  { value: 'US', label: '🇺🇸 United States' },
  { value: 'KR', label: '🇰🇷 South Korea' },
  { value: 'UK', label: '🇬🇧 United Kingdom' },
  { value: 'JP', label: '🇯🇵 Japan' },
];

const CATEGORIES_BY_PLATFORM: Record<SalesPlatform, { value: string; label: string }[]> = {
  tiktokshop: [
    { value: 'Beauty Supplement', label: 'Beauty Supplement' },
    { value: 'Vitamins & Supplements', label: 'Vitamins & Supplements' },
    { value: 'Collagen & Anti-Aging', label: 'Collagen & Anti-Aging' },
    { value: 'Weight Management', label: 'Weight Management' },
    { value: 'Sports Nutrition', label: 'Sports Nutrition' },
    { value: 'Skincare', label: 'Skincare' },
    { value: 'Hair Care & Styling', label: 'Hair Care & Styling' },
    { value: 'Makeup & Cosmetics', label: 'Makeup & Cosmetics' },
    { value: 'Fragrance', label: 'Fragrance' },
    { value: 'Bath & Body', label: 'Bath & Body' },
  ],
  amazon: [
    { value: 'Beauty Supplement', label: 'Beauty Supplement' },
    { value: 'Vitamins & Dietary Supplements', label: 'Vitamins & Dietary Supplements' },
    { value: 'Collagen Peptides', label: 'Collagen Peptides' },
    { value: 'Weight Loss Supplements', label: 'Weight Loss Supplements' },
    { value: 'Sports Nutrition', label: 'Sports Nutrition' },
    { value: 'Skincare', label: 'Skincare' },
    { value: 'Hair Care', label: 'Hair Care' },
    { value: 'Makeup', label: 'Makeup' },
  ],
};

const METRIC_OPTIONS = [
  { value: 'Revenue Score', label: 'Revenue Score', tiktokOnly: true },
  { value: 'View Score', label: 'View Score (Reach)', tiktokOnly: false },
  { value: 'Engagement Score', label: 'Engagement Score', tiktokOnly: false },
];

const STEPS_META_KO = [
  { key: 'input', label: '기본 정보' },
  { key: 'benchmark', label: '벤치마크 선택' },
  { key: 'results', label: '전략 결과' },
] as const;

const STEPS_META_EN = [
  { key: 'input', label: 'Basic Info' },
  { key: 'benchmark', label: 'Benchmark' },
  { key: 'results', label: 'Results' },
] as const;

function getStepIndex(step: PlanningStep): number {
  if (step === 'input') return 0;
  if (step === 'loading-12' || step === 'benchmark') return 1;
  return 2;
}

const LOADING_12_MSGS_TIKTOK_KO = [
  'TikTokShop US 데이터 조회 중',
  'Health 카테고리로 한정',
  'Beauty Supplement 카테고리로 한정',
  '관련 키워드로 검색',
  '관련 브랜드·상품 정리 중',
];

const LOADING_12_MSGS_TIKTOK_EN = [
  'Fetching TikTokShop US data',
  'Filtering to Health category',
  'Filtering to Beauty Supplement',
  'Searching relevant keywords',
  'Compiling relevant brands & products',
];

const LOADING_12_MSGS_AMAZON_KO = [
  'Amazon US 베스트셀러 조회 중',
  'Health 카테고리로 한정',
  'Beauty Supplement 카테고리로 한정',
  '관련 키워드로 검색',
  '관련 브랜드·상품 정리 중',
];

const LOADING_12_MSGS_AMAZON_EN = [
  'Fetching Amazon US bestsellers',
  'Filtering to Health category',
  'Filtering to Beauty Supplement',
  'Searching relevant keywords',
  'Compiling relevant brands & products',
];

const LOADING_23_MSGS_KO = [
  '분석 데이터셋 확정',
  '분석할 Post 데이터 수집',
  'Post 영상 분석',
  '주요 변인 파악 중',
  '가설 검증 및 주요 성공요인 리스트업 중',
  '최종 문서 작성 중',
];

const LOADING_23_MSGS_EN = [
  'Finalizing analysis dataset',
  'Collecting post data to analyze',
  'Analyzing post videos',
  'Identifying key variables',
  'Validating hypotheses & listing success factors',
  'Generating final report',
];

export function CampaignPlanningPage() {
  const { workspace } = useWorkspace();
  const lang = workspace.language === 'ko' ? 'ko' : 'en';
  const isKo = lang === 'ko';

  const STEPS_META = isKo ? STEPS_META_KO : STEPS_META_EN;
  const LOADING_12_MSGS_TIKTOK = isKo ? LOADING_12_MSGS_TIKTOK_KO : LOADING_12_MSGS_TIKTOK_EN;
  const LOADING_12_MSGS_AMAZON = isKo ? LOADING_12_MSGS_AMAZON_KO : LOADING_12_MSGS_AMAZON_EN;
  const LOADING_23_MSGS = isKo ? LOADING_23_MSGS_KO : LOADING_23_MSGS_EN;

  const [step, setStep] = useState<PlanningStep>('input');
  const [form, setForm] = useState<FormValues>({
    country: 'US',
    platform: 'TikTok',
    brandProduct: 'Beauty Supplement',
    metric: 'Revenue Score',
    salesPlatform: 'tiktokshop',
  });
  const [submittedForm, setSubmittedForm] = useState<FormValues | null>(null);
  const [benchmarkSelection, setBenchmarkSelection] = useState<BenchmarkSelection | null>(null);

  const handleSalesPlatformChange = (sp: SalesPlatform) => {
    const categories = CATEGORIES_BY_PLATFORM[sp];
    const categoryExists = categories.some((c) => c.value === form.brandProduct);
    const nextMetric =
      sp !== 'tiktokshop' && form.metric === 'Revenue Score' ? 'View Score' : form.metric;
    setForm((f) => ({
      ...f,
      salesPlatform: sp,
      brandProduct: categoryExists ? f.brandProduct : categories[0].value,
      metric: nextMetric,
    }));
  };

  const handleInputNext = () => {
    setStep('loading-12');
  };

  const handleBenchmarkNext = (selection: BenchmarkSelection) => {
    setSubmittedForm({ ...form });
    setBenchmarkSelection(selection);
    setStep('loading-23');
  };

  const currentStepIndex = getStepIndex(step);
  const loading12Msgs =
    form.salesPlatform === 'tiktokshop' ? LOADING_12_MSGS_TIKTOK : LOADING_12_MSGS_AMAZON;

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 border-b px-6 py-4">
        {step !== 'input' && (
          <Button
            variant="ghost"
            size="icon"
            className="size-8 shrink-0"
            onClick={() => {
              if (step === 'benchmark' || step === 'loading-12') setStep('input');
              else if (step === 'results' || step === 'loading-23') setStep('benchmark');
            }}
          >
            <ArrowLeft className="size-4" />
          </Button>
        )}
        <div className="flex items-center gap-2">
          <Lightbulb className="size-5 text-primary" />
          <div>
            <h1 className="text-base font-semibold leading-tight">Campaign Planning</h1>
            <p className="text-xs text-muted-foreground">
              Benchmark top competitors to shape your pre-campaign strategy
            </p>
          </div>
        </div>

        {/* Step indicator */}
        {step !== 'results' && (
          <div className="ml-auto flex items-center gap-1.5">
            {STEPS_META.map((s, i) => (
              <div key={s.key} className="flex items-center gap-1.5">
                <div
                  className={cn(
                    'flex size-5 items-center justify-center rounded-full text-[10px] font-bold transition-colors',
                    i <= currentStepIndex
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground',
                  )}
                >
                  {i < currentStepIndex ? (
                    <svg className="size-3" viewBox="0 0 10 10" fill="none">
                      <path
                        d="M2 5l2.5 2.5 3.5-4"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    i + 1
                  )}
                </div>
                <span
                  className={cn(
                    'hidden text-xs sm:block',
                    i === currentStepIndex ? 'font-medium text-foreground' : 'text-muted-foreground',
                  )}
                >
                  {s.label}
                </span>
                {i < STEPS_META.length - 1 && <div className="h-px w-4 bg-border" />}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-6 overflow-auto p-6">
        {/* Step 1: Input */}
        {step === 'input' && (
          <div className="flex flex-col gap-6 max-w-2xl">
            <div className="rounded-xl border bg-muted/30 p-4">
              <p className="text-sm font-medium text-foreground">
                {isKo ? '캠페인 시작 전 경쟁사를 벤치마킹하세요' : 'Benchmark competitors before your campaign'}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {isKo
                  ? '같은 카테고리 최상위 어필리에이트 포스트를 분석해 어떤 크리에이터·포맷·콘텐츠 변수가 성과를 만드는지 파악하고, 데이터 기반 브리핑 자료를 생성합니다.'
                  : 'Analyze top affiliate posts in your category to identify which creator, format, and content variables drive performance — then generate a data-backed briefing.'}
              </p>
            </div>

            <div className="flex flex-col gap-5">
              {/* 1. Sales Platform */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-foreground">{isKo ? '판매 플랫폼' : 'Sales Platform'}</label>
                <div className="flex gap-3">
                  <SalesPlatformButton
                    active={form.salesPlatform === 'tiktokshop'}
                    disabled={false}
                    onClick={() => handleSalesPlatformChange('tiktokshop')}
                    label="TikTok Shop"
                    sub={isKo ? '베스트셀러 데이터' : 'Bestseller data'}
                    color="bg-black"
                    imgPath="/campaign-planning/tiktok-logo.svg"
                    abbr="TT"
                  />
                  <SalesPlatformButton
                    active={form.salesPlatform === 'amazon'}
                    disabled={true}
                    onClick={() => {}}
                    label="Amazon"
                    sub="Bestseller data"
                    color="bg-[#FF9900]"
                    imgPath="/campaign-planning/amazon-logo.svg"
                    abbr="a"
                  />
                </div>
              </div>

              {/* 2. Market */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-foreground">{isKo ? '마켓' : 'Market'}</label>
                <Select
                  value={form.country}
                  onValueChange={(v) => setForm((f) => ({ ...f, country: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRY_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* 3. Product Category (platform-specific dropdown) */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-foreground">{isKo ? '제품 카테고리' : 'Product Category'}</label>
                <Select
                  value={form.brandProduct}
                  onValueChange={(v) => setForm((f) => ({ ...f, brandProduct: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES_BY_PLATFORM[form.salesPlatform].map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* 4. Metric */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-foreground">{isKo ? '최적화 지표' : 'Optimization Metric'}</label>
                <Select
                  value={form.metric}
                  onValueChange={(v) => setForm((f) => ({ ...f, metric: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {METRIC_OPTIONS.map((o) => {
                      const isDisabled = o.tiktokOnly && form.salesPlatform !== 'tiktokshop';
                      return (
                        <SelectItem key={o.value} value={o.value} disabled={isDisabled}>
                          <span className={cn(isDisabled && 'text-muted-foreground')}>
                            {o.label}
                          </span>
                          {isDisabled && (
                            <span className="ml-2 text-[10px] text-muted-foreground">
                              TikTok Shop only
                            </span>
                          )}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <Button
                onClick={handleInputNext}
                className="gap-2 self-start"
                size="lg"
              >
                {isKo ? '다음' : 'Next'}
                <ChevronRight className="size-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Loading 1→2 */}
        {step === 'loading-12' && (
          <div className="flex flex-col gap-6 max-w-2xl">
            <div className="rounded-xl border bg-muted/30 p-4">
              <p className="text-sm font-medium text-foreground">
                {form.salesPlatform === 'tiktokshop'
                  ? (isKo ? 'TikTok Shop 상위 판매 브랜드를 추천하고 있어요' : 'Finding top-selling brands on TikTok Shop')
                  : (isKo ? 'Amazon 베스트셀러 브랜드를 조회하고 있어요' : 'Looking up top-selling brands on Amazon')}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {isKo
                  ? `${form.brandProduct} 카테고리 기준으로 벤치마크 대상을 선정합니다`
                  : `Selecting benchmark targets in the ${form.brandProduct} category`}
              </p>
            </div>
            <StepProgressLoader
              messages={loading12Msgs}
              intervalMs={900}
              onComplete={() => setStep('benchmark')}
            />
          </div>
        )}

        {/* Step 2: Benchmark Selection */}
        {step === 'benchmark' && (
          <div className="max-w-2xl">
            <BenchmarkSelector
              salesPlatform={form.salesPlatform}
              onNext={handleBenchmarkNext}
              onBack={() => setStep('input')}
            />
          </div>
        )}

        {/* Loading 2→3 */}
        {step === 'loading-23' && (
          <div className="flex flex-col gap-6 max-w-2xl">
            <div className="rounded-xl border bg-muted/30 p-4">
              <p className="text-sm font-medium text-foreground">
                {isKo ? '선정된 포스트 데이터를 분석하고 있어요' : 'Analyzing selected post data'}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {isKo ? '성과 변인을 파악하고 캠페인 전략을 도출합니다' : 'Identifying performance drivers and building campaign strategy'}
              </p>
            </div>
            <StepProgressLoader
              messages={LOADING_23_MSGS}
              intervalMs={900}
              onComplete={() => setStep('results')}
            />
          </div>
        )}

        {/* Step 3: Results */}
        {step === 'results' && submittedForm && (
          <div className="flex flex-col gap-4">
            <PlanningResults
              report={beautySupplementPlanning}
              inputSummary={submittedForm}
              benchmarkProductCount={benchmarkSelection?.selectedProductIds.size ?? 0}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function SalesPlatformButton({
  active,
  disabled,
  onClick,
  label,
  sub,
  color,
  imgPath,
  abbr,
}: {
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  label: string;
  sub: string;
  color: string;
  imgPath?: string;
  abbr: string;
}) {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={cn(
        'flex flex-1 items-center gap-3 rounded-xl border p-3.5 transition-colors text-left',
        disabled
          ? 'cursor-not-allowed opacity-40 border-border bg-card'
          : active
            ? 'border-primary bg-primary/5'
            : 'border-border bg-card hover:bg-muted/40',
      )}
    >
      <div
        className={cn(
          'flex size-9 shrink-0 items-center justify-center rounded-lg',
          disabled ? 'bg-muted' : color,
        )}
      >
        {imgPath ? (
          <img src={imgPath} alt={label} className={cn('size-5 object-contain', disabled && 'opacity-50')} />
        ) : (
          <span className="text-white text-sm font-bold">{abbr}</span>
        )}
      </div>
      <div>
        <p className="text-sm font-semibold">{label}</p>
        <div className="flex items-center gap-1">
          <p className="text-xs text-muted-foreground">{sub}</p>
          {disabled && (
            <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground/60">
              <Lock className="size-2.5" />
              Coming soon
            </span>
          )}
        </div>
      </div>
      {active && !disabled && (
        <div className="ml-auto flex size-4 items-center justify-center rounded-full bg-primary">
          <svg className="size-2.5 text-primary-foreground" viewBox="0 0 10 10" fill="none">
            <path
              d="M2 5l2.5 2.5 3.5-4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      )}
    </button>
  );
}
